"use client";

import { useEffect, useRef, useState } from "react";
import { type CountryCode } from "@/config/country-config";
import { useRuntimeConfig } from "@/config/runtime-config-context";
import { LogEvent } from "@/datadog/events";
import { sendClientLog } from "@/datadog/utils/send-client-log";
import { isValidSwedishFullAccountNumber } from "@/features/customer-information/utils/swedish-account-fields";
import {
	EMPTY_TRUSTED_ACCOUNT_FIELDS,
	type TrustedAccountFields,
	resolveTrustedAccountFields,
} from "@/features/customer-information/utils/trusted-account";
import { TaskType } from "@/types";

interface UseTrustedAccountFieldsArgs {
	accountNumbers: string[] | undefined;
	flowId: string | undefined;
	country: CountryCode;
}

export const useTrustedAccountFields = ({
	accountNumbers,
	flowId,
	country,
}: UseTrustedAccountFieldsArgs): TrustedAccountFields => {
	const [fields, setFields] = useState<TrustedAccountFields>(
		EMPTY_TRUSTED_ACCOUNT_FIELDS
	);
	const hasResolved = useRef(false);
	const { testEnvironment } = useRuntimeConfig();

	useEffect(() => {
		// Resolving twice would consume an already-removed Tink account and blank
		// the field, so the first run wins.
		if (hasResolved.current) return;
		hasResolved.current = true;

		const resolved = resolveTrustedAccountFields(
			accountNumbers,
			flowId,
			country,
			testEnvironment
		);
		setFields(resolved);

		if (
			resolved.source === "tink" &&
			resolved.isWholeAccountNumber &&
			!isValidSwedishFullAccountNumber(resolved.accountNumber)
		) {
			// Shape only: the number itself is sensitive and gets redacted anyway.
			void sendClientLog({
				level: "warn",
				event: LogEvent.TinkAccountInvalid,
				message:
					"Tink verified account number is not a valid Swedish account number",
				task: TaskType.CustomerInformation,
				flowId,
				context: { digitCount: resolved.accountNumber.length },
			});
		}
	}, [accountNumbers, flowId, country, testEnvironment]);

	return fields;
};
